/**
 * Structured, step-by-step, REDACTED login logging to the browser console.
 *
 * Used by Login.vue, services/microsoftAuth.js and the customer store login
 * actions so that when ANY user has a login problem we can read exactly what
 * happened, step by step, in their browser console. Each attempt gets a short
 * `loginAttemptId` which is ALSO sent to the backend (as a `?loginAttemptId=`
 * query param on /microsoft-login and /google-login) so the browser trace lines
 * up with the backend CloudWatch logs for the same attempt.
 *
 * Every log line is `[LOGIN][<provider>][<step>] +<elapsed>ms id=<attemptId>`
 * so it is easy to filter in the console.
 *
 * SECURITY: detail objects are passed through redactLoginDetail() so we never
 * print access/id tokens, passwords, Firebase tokens, OAuth codes, etc.
 *
 * KEEP IN SYNC WITH cypress/node-tests/login-logger.test.js.
 */

// Keys whose values must never be printed (compared case-insensitively).
const SENSITIVE_KEYS = [
  'accesstoken', 'idtoken', 'token', 'firebasetoken', 'customtoken',
  'password', 'credential', 'authorization', 'code', 'code_verifier',
  'code_challenge',
];

// Returns a deep copy of `detail` with any sensitive values masked. Non-objects
// pass through unchanged; the original object is never mutated.
export function redactLoginDetail(detail) {
  if (detail === null || typeof detail !== 'object') return detail;
  const out = Array.isArray(detail) ? [] : {};
  for (const k of Object.keys(detail)) {
    const v = detail[k];
    if (SENSITIVE_KEYS.includes(String(k).toLowerCase())) {
      out[k] = typeof v === 'string' ? `[redacted len=${v.length}]` : '[redacted]';
    } else if (v && typeof v === 'object') {
      out[k] = redactLoginDetail(v);
    } else {
      out[k] = v;
    }
  }
  return out;
}

export function formatLoginPrefix(provider, step, id, elapsedMs) {
  return `[LOGIN][${provider}][${step}] +${elapsedMs}ms id=${id}`;
}

// Short, human-greppable id for one login attempt, e.g. lgn_lt4k9a_x7f2qd.
export function newLoginAttemptId() {
  const t = Date.now().toString(36);
  const r = Math.random().toString(36).slice(2, 8);
  return `lgn_${t}_${r}`;
}

/**
 * Create a logger bound to one login attempt.
 * @param {string} provider - 'microsoft' | 'google'
 * @param {string} [attemptId] - reuse an existing id, else one is generated
 * @returns {{ id: string, step: Function, error: Function }}
 */
export function createLoginLogger(provider, attemptId) {
  const id = attemptId || newLoginAttemptId();
  const start = Date.now();

  const step = (stepName, detail) => {
    const prefix = formatLoginPrefix(provider, stepName, id, Date.now() - start);
    if (detail === undefined) {
      console.log(prefix);
    } else {
      console.log(prefix, redactLoginDetail(detail));
    }
  };

  // Genuine failures (NOT benign user-cancellations — log those via step()).
  const error = (stepName, err) => {
    const prefix = `[LOGIN][${provider}][${stepName}][ERROR] +${Date.now() - start}ms id=${id}`;
    console.error(prefix, {
      name: err && err.name,
      code: err && (err.code || err.errorCode),
      message: err && err.message,
      httpStatus: err && err.response && err.response.status,
      responseData: err && err.response && redactLoginDetail(err.response.data),
    });
  };

  return { id, step, error };
}
